/**
 * Comfort vignette and fade: a camera-attached quad with a radial alpha gradient (vignette)
 * and a solid black layer (fade). The only post-process-like effect allowed by the budget.
 */
import * as THREE from 'three';

const vertex = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const fragment = /* glsl */ `
  uniform float vignette;
  uniform float fade;
  varying vec2 vUv;
  void main() {
    float d = distance(vUv, vec2(0.5)) * 2.0;
    float ring = smoothstep(0.55, 1.05, d) * vignette;
    float a = max(ring, fade);
    gl_FragColor = vec4(0.0, 0.0, 0.0, a);
  }
`;

export class Vignette {
  readonly mesh: THREE.Mesh;
  private readonly uniforms = { vignette: { value: 0 }, fade: { value: 1 } };
  private targetVignette = 0;
  private fadeTarget = 0;
  private fadeSpeed = 2;
  private onFadeDone: (() => void) | undefined;

  constructor(camera: THREE.Camera) {
    const material = new THREE.ShaderMaterial({
      uniforms: this.uniforms,
      vertexShader: vertex,
      fragmentShader: fragment,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });
    this.mesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material);
    this.mesh.name = 'vignette';
    this.mesh.position.set(0, 0, -0.3);
    this.mesh.scale.set(0.9, 0.9, 1);
    this.mesh.renderOrder = 10_000;
    this.mesh.frustumCulled = false;
    camera.add(this.mesh);
  }

  /** 0..1 strength that is reached smoothly. */
  setStrength(value: number): void {
    this.targetVignette = THREE.MathUtils.clamp(value, 0, 1);
  }

  /** Instant pulse (snap turn, teleport) that decays. */
  pulse(value = 0.8): void {
    this.uniforms.vignette.value = Math.max(this.uniforms.vignette.value, value);
  }

  /** Fade to black (1) or clear (0); resolves when done. */
  fadeTo(value: 0 | 1, seconds = 0.5): Promise<void> {
    this.fadeTarget = value;
    this.fadeSpeed = 1 / Math.max(0.05, seconds);
    return new Promise((resolve) => {
      this.onFadeDone = resolve;
    });
  }

  setFadeImmediate(value: number): void {
    this.uniforms.fade.value = value;
    this.fadeTarget = value;
  }

  update(dt: number): void {
    const v = this.uniforms.vignette;
    v.value += (this.targetVignette - v.value) * Math.min(1, dt * 10);
    if (v.value > this.targetVignette) v.value = Math.max(this.targetVignette, v.value - dt * 2.5);
    const f = this.uniforms.fade;
    if (f.value !== this.fadeTarget) {
      const step = this.fadeSpeed * dt;
      f.value =
        f.value < this.fadeTarget
          ? Math.min(this.fadeTarget, f.value + step)
          : Math.max(this.fadeTarget, f.value - step);
      if (f.value === this.fadeTarget && this.onFadeDone) {
        const done = this.onFadeDone;
        this.onFadeDone = undefined;
        done();
      }
    }
    this.mesh.visible = v.value > 0.002 || f.value > 0.002;
  }
}
